import { ArrowNarrowDown } from "@untitledui/icons";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useCreatorCohorts } from "@/api/creator-cohorts";
import { Avatar } from "@/components/base/avatar/avatar";
import { useCollabTriggerTypes } from "@/pages/sequences/collab-trigger-types";
import { TRIGGER_LABELS } from "@/pages/sequences/sequence-steps-preview";
import {
  buildInvitedCreatorSegments,
  categorizeInvitedCreator,
  InvitedCreatorsDistribution,
} from "../../components/InvitedCreatorsDistribution";
import { getCategoryColor } from "../../components/ProgressBar";
import type { CreateCollabFormValues } from "../create-collab-group-modal";
import { entryToMode } from "./AutomationStep5";

const MODE_LABELS = { disabled: "Disabled", default: "Default", custom: "Custom" } as const;

const dateFormatter = new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit" });

const CardHeader = ({ title }: { title: string }) => (
  <div className="flex items-center justify-between">
    <span className="text-md font-medium text-secondary">{title}</span>
  </div>
);

export const ReviewStep6 = ({ showInvitedCreators = true }: { showInvitedCreators?: boolean }) => {
  const { watch } = useFormContext<CreateCollabFormValues>();
  const values = watch();
  const { data: cohorts = [] } = useCreatorCohorts();
  const triggerTypes = useCollabTriggerTypes();
  const cohortNameById = new Map(cohorts.map((cohort) => [cohort.id, cohort.name]));
  const invitedCreatorSegments = buildInvitedCreatorSegments(values.invitedCreators, cohortNameById);

  const invitedCreatorsScrollRef = useRef<HTMLDivElement>(null);
  const [isAtScrollEnd, setIsAtScrollEnd] = useState(false);

  const checkScrollEnd = (el: HTMLDivElement) => {
    setIsAtScrollEnd(el.scrollHeight - el.scrollTop - el.clientHeight < 4);
  };

  useEffect(() => {
    if (invitedCreatorsScrollRef.current) checkScrollEnd(invitedCreatorsScrollRef.current);
  }, [values.invitedCreators]);

  const approvalRuleLabel =
    values.approvalRule === "auto-approve"
      ? "Auto-approve"
      : values.approvalRule === "manually-review"
        ? "Manually Review"
        : "Not set";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <p className="text-md font-semibold text-primary">Review</p>
        <p className="text-sm text-quaternary">Check everything looks good before launching your campaign.</p>
      </div>

      <div className="bg-white border border-border-secondary rounded-xl p-4 flex flex-col gap-4">
        <CardHeader title="Campaign Details" />
        <div className="flex flex-col gap-2">
          {[
            { label: "Campaign name", value: values.collaborationName || "—" },
            {
              label: "Active Until",
              value: values.activeUntil ? dateFormatter.format(new Date(values.activeUntil)) : "—",
            },
            { label: "Type Of Collab", value: "Target" },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-center justify-between">
              <span className="text-sm text-tertiary">{label}</span>
              <span className="text-sm font-medium text-secondary">{value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border border-border-secondary rounded-xl p-4 flex flex-col gap-4">
        <CardHeader title="Product" />
        {values.products.length === 0 ? (
          <span className="text-sm text-tertiary">No products added yet.</span>
        ) : (
          values.products.map((product, i) => (
            <div key={product.id}>
              {i > 0 && <hr className="border-0 border-t border-border-secondary mb-4" />}
              <div className="flex items-center gap-3">
                {product.thumbnail ? (
                  <img src={product.thumbnail} alt="" className="w-[46px] h-[46px] rounded-lg shrink-0 object-cover" />
                ) : (
                  <div className="w-[46px] h-[46px] rounded-lg shrink-0 bg-gray-100" />
                )}
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-primary">{product.name}</span>
                  <span className="text-sm text-tertiary">{product.commissionRate || 0}% Commission Rate</span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="bg-white border border-border-secondary rounded-xl p-4 flex flex-col gap-4">
        <CardHeader title="Samples" />
        <div className="flex items-center justify-between">
          <span className="text-sm text-tertiary">Offer Free Samples</span>
          <span
            className={
              values.offerFreeSamples
                ? "inline-flex items-center px-2 py-0.5 rounded-full bg-success-50 border border-success-200 text-xs font-medium text-success-700"
                : "inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-xs font-medium text-gray-600"
            }
          >
            {values.offerFreeSamples ? "ON" : "OFF"}
          </span>
        </div>
        {values.offerFreeSamples && (
          <>
            <hr className="border-0 border-t border-border-secondary" />
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-secondary">Approval Rules</span>
            </div>
            <span className="text-sm text-secondary">{approvalRuleLabel}</span>
          </>
        )}
      </div>

      {showInvitedCreators && (
        <div className="bg-white border border-border-secondary rounded-xl p-4 flex flex-col gap-4">
          <CardHeader title={`Invited Creators (${values.invitedCreators.length})`} />
          {values.invitedCreators.length === 0 ? (
            <span className="text-sm text-tertiary">No creators selected yet.</span>
          ) : (
            <>
              <InvitedCreatorsDistribution creators={values.invitedCreators} />
              <hr className="border-0 border-t border-border-secondary" />
              <div className="relative">
                <div
                  ref={invitedCreatorsScrollRef}
                  className="flex flex-col gap-3 max-h-[280px] overflow-y-auto"
                  onScroll={(e) => checkScrollEnd(e.currentTarget)}
                >
                  {values.invitedCreators.map((creator) => {
                    const category = categorizeInvitedCreator(creator, cohortNameById);
                    const color = getCategoryColor(category, invitedCreatorSegments);
                    return (
                      <div key={creator.id} className="flex items-center gap-3">
                        <Avatar src={creator.avatar ?? undefined} initials={creator.initials} size="sm" />
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="text-sm font-medium text-primary truncate">{creator.name}</span>
                          <span className="text-sm text-tertiary truncate">{creator.handle}</span>
                        </div>
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full border text-xs font-medium shrink-0"
                          style={{ backgroundColor: `${color}1A`, borderColor: `${color}66`, color }}
                        >
                          {category}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {!isAtScrollEnd && (
                  <>
                    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                    <div className="absolute bottom-0 left-0 right-0 flex items-end justify-center pb-1 pointer-events-none">
                      <span className="flex items-center gap-1 text-xs font-medium text-quaternary">
                        Scroll to See More
                        <ArrowNarrowDown className="w-4 h-4" />
                      </span>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}

      <div className="bg-white border border-border-secondary rounded-xl p-4 flex flex-col gap-4">
        <CardHeader title="Automation Triggers" />
        {triggerTypes.map((triggerType, i) => (
          <div key={triggerType}>
            {i > 0 && <hr className="border-0 border-t border-border-secondary mb-4" />}
            <div className="flex items-center justify-between">
              <span className="text-sm text-secondary">{TRIGGER_LABELS[triggerType] ?? triggerType}</span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-xs font-medium text-gray-600">
                {MODE_LABELS[entryToMode(values.automations[triggerType])]}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
